import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { type StateStorage, createJSONStorage, persist } from 'zustand/middleware';

import { paths } from '~/constants/paths';

/**
 * Versions of the intake-first flow, switchable live for review:
 * - `current`: coverage must be verified before items can be chosen.
 * - `clinical`: the clinical team's proposal — never block, always invite.
 *   Medicaid-only is a first-class path, unverified coverage is a soft
 *   warning, and uncovered items are shown locked rather than hidden.
 * - `d2c`: mirrors the Greenhouse D2C intake. Demographics first so discovery
 *   can start, self-reported coverage gates the catalog, and discovery results
 *   are reviewed in their own step after clinical details.
 */
export type IntakeVariant = 'current' | 'clinical' | 'd2c';

/**
 * The switch between versions is off unless `VITE_INTAKE_VARIANT_SWITCH=true`,
 * so the shared preview shows only the D2C mirror.
 */
export function isVariantSwitchEnabled(): boolean {
  return import.meta.env.VITE_INTAKE_VARIANT_SWITCH === 'true';
}

const effectiveVariant = (variant: IntakeVariant): IntakeVariant =>
  isVariantSwitchEnabled() ? variant : 'd2c';

const noopStorage: StateStorage = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
};

type IntakeVariantState = {
  variant: IntakeVariant;
  setVariant: (variant: IntakeVariant) => void;
};

export const useIntakeVariantStore = create<IntakeVariantState>()(
  persist((set) => ({ variant: 'd2c', setVariant: (variant) => set({ variant }) }), {
    name: 'conduit-intake-variant',
    // Same SSR constraint as the eligibility store: without storage, persist
    // never attaches its API.
    storage: createJSONStorage(() => (typeof window === 'undefined' ? noopStorage : localStorage)),
    skipHydration: true,
  })
);

/**
 * Callers that gate navigation on the variant must wait for `hydrated`, or a
 * refresh would apply the `current` rules before the saved choice loads.
 */
export function useIntakeVariant() {
  const [hydrated, setHydrated] = useState(() => useIntakeVariantStore.persist.hasHydrated());

  useEffect(() => {
    const unsubscribe = useIntakeVariantStore.persist.onFinishHydration(() => setHydrated(true));
    void useIntakeVariantStore.persist.rehydrate();
    return unsubscribe;
  }, []);

  const variant = effectiveVariant(useIntakeVariantStore((state) => state.variant));
  const setVariant = useIntakeVariantStore((state) => state.setVariant);

  return {
    variant,
    // The D2C mirror keeps the clinical version's soft gating and locked items.
    isClinical: variant === 'clinical' || variant === 'd2c',
    isD2c: variant === 'd2c',
    setVariant,
    hydrated,
  };
}

/** For non-render code (event handlers) that runs after hydration. */
export function getIntakeVariant(): IntakeVariant {
  return effectiveVariant(useIntakeVariantStore.getState().variant);
}

/** The D2C mirror reviews discovered insurance before the order review. */
export function pathAfterClinicalDetails(): string {
  return getIntakeVariant() === 'd2c' ? paths.checkoutInsurance : paths.checkoutReview;
}
